<script setup>
import { computed, onMounted, ref } from "vue"
import { useAuthStore } from "~/stores/auth"
import { useNotificationsStore } from "~/stores/notifications"

const authStore = useAuthStore()
const notificationsStore = useNotificationsStore()
const route = useRoute()

// Client-side only flag to prevent hydration mismatch
const isMounted = ref(false)

// Use store getter for visible banners
const visibleBanners = computed(() => notificationsStore.visibleBanners)

// Hide banners on admin settings page and auth pages
const shouldShowBanners = computed(() => {
  // Only show after mount to prevent hydration mismatch
  if (!isMounted.value) {
    return false
  }
  // Don't show on admin settings page (where banners are being managed)
  if (route.path === "/admin/settings" || route.path === "/admin/settings/") {
    return false
  }
  // Don't show on auth pages to avoid 401 redirects
  if (route.path.startsWith("/auth")) {
    return false
  }
  return visibleBanners.value.length > 0
})

// Fetch banners on mount
const fetchBanners = async () => {
  const userRole = authStore.user?.role || "shipper"
  await notificationsStore.fetchSystemBanners(userRole)
}

// Dismiss a banner using store action
const dismissBanner = async (bannerId) => {
  await notificationsStore.dismissSystemBanner(bannerId)
}

const getBannerClass = (type) => {
  const classes = {
    info: "bg-blue-50/20 border-blue-200 text-blue-900",
    warning: "bg-yellow-50/20 border-yellow-200 text-yellow-900",
    error: "bg-red-50/20 border-red-200 text-red-900",
    success: "bg-green-50/20 border-green-200 text-green-900",
  }
  return classes[type] || classes.info
}

const getIcon = (type) => {
  const icons = {
    info: "ph:info",
    warning: "ph:warning",
    error: "ph:x-circle",
    success: "ph:check-circle",
  }
  return icons[type] || icons.info
}

onMounted(() => {
  isMounted.value = true
  // Skip fetching banners on auth pages to avoid 401 redirects
  if (!route.path.startsWith("/auth")) {
    fetchBanners()
  }
})
</script>

<template>
  <!-- Fixed position container - bottom right corner -->
  <ClientOnly>
    <Teleport to="body">
      <div v-if="shouldShowBanners" class="fixed bottom-4 right-4 z-[100] pointer-events-none">
        <div class="space-y-3 pointer-events-auto w-full max-w-sm">
          <TransitionGroup name="banner" tag="div" class="space-y-3">
            <div v-for="banner in visibleBanners" :key="banner.id" :class="getBannerClass(banner.type)" class="rounded-xl shadow-lg backdrop-blur-sm flex items-start justify-between p-3 sm:p-4 border w-full">
              <div class="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
                <Icon :name="getIcon(banner.type)" :size="18" class="mt-0.5 flex-shrink-0 opacity-80" />
                <div class="flex-1 min-w-0">
                  <h4 class="font-medium text-sm sm:text-base">{{ banner.title }}</h4>
                  <p class="text-xs sm:text-sm mt-1 opacity-80 leading-relaxed">{{ banner.message }}</p>
                </div>
              </div>
              <button v-if="banner.metadata?.dismissible !== false" @click="dismissBanner(banner.id)" class="text-gray-500 hover:text-gray-700 ml-2 sm:ml-3 flex-shrink-0 transition-colors p-1 hover:bg-gray-100 rounded-lg">
                <Icon name="ph:x" :size="16" />
              </button>
            </div>
          </TransitionGroup>
        </div>
      </div>
    </Teleport>
    <template #fallback>
      <!-- Empty fallback during SSR -->
    </template>
  </ClientOnly>
</template>

<style scoped>
/* Banner enter/leave transitions */
.banner-enter-active,
.banner-leave-active {
  transition: all 0.3s ease;
}

.banner-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.banner-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.banner-move {
  transition: transform 0.3s ease;
}
</style>
